import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * The notification bell — #notifBell + #notifDrop + toggleNotifs() (index.html:1240).
 *
 * A 🔔 with an unread `.ndot` badge and a fixed dropdown of `.notif-item`s, fed
 * by GET /notifications (polled). Opening the dropdown marks everything read
 * (POST /notifications/read), exactly as the prototype does on open, then the
 * badge clears. Outside-click and Esc close it. Rendered only when signed in —
 * the feed is per-user.
 */
interface Notif {
  id: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get<{ notifications: Notif[]; unread: number }>('/notifications')).data,
    refetchInterval: 60_000, // pick up new notifications without a reload
    refetchOnWindowFocus: true,
  });
  const notifs = data?.notifications ?? [];
  const unread = data?.unread ?? 0;

  // Opening marks all read (toggleNotifs :1244), then the badge clears.
  async function toggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post('/notifications/read');
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  }

  // Outside-click + Esc close (prototype :1869).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button className="icon-btn" id="notifBell" data-testid="notif-bell" aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`} onClick={() => void toggle()}>
        🔔
        {unread > 0 ? (
          <span className="ndot" data-testid="notif-count">
            {unread}
          </span>
        ) : null}
      </button>
      {/* #notifDrop is position:fixed (top-right) per v02.css; toggled by display.
          Contents render only while OPEN — the prototype fills it on open
          (toggleNotifs :1244), and keeping notification bodies out of the DOM
          until then avoids leaking them into any page's HTML. */}
      <div id="notifDrop" data-testid="notif-drop" style={{ display: open ? 'block' : 'none' }}>
        {open ? (
          notifs.length ? (
            notifs.map((n) => (
              <div className="notif-item" key={n.id}>
                <b>✉ {n.title}</b>
                <p>{n.body}</p>
                <span>{new Date(n.createdAt).toLocaleString()}</span>
              </div>
            ))
          ) : (
            <div className="notif-item">
              <p>No notifications yet. Enrolling, a purchase, or a mentor answering your question will show up here.</p>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
